import { useState, useEffect } from 'react'
import { useFetchData } from '../../hooks/useFetchData'
import { scoreMCQ } from '../../utils/evaluator'
import { 
  CheckCircle, XCircle, Loader, AlertCircle, Award, Flame, 
  Target, TrendingUp, BarChart3, Calendar, Zap, Brain,
  Filter, Image as ImageIcon, BookOpen, Trophy
} from 'lucide-react'

export default function OIRPracticeEnhanced({ state, updateState }) {
  const { data, loading, error } = useFetchData('/data/oir_questions.json')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [score, setScore] = useState(0)
  const [attempted, setAttempted] = useState(0)
  const [streak, setStreak] = useState(0)
  const [showStats, setShowStats] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [difficulty, setDifficulty] = useState('all')
  const [dailyGoal] = useState(20) // 20 questions per day
  const [answeredToday, setAnsweredToday] = useState(0)

  const questions = data?.questions || []
  
  // Filter questions based on type and difficulty
  const filteredQuestions = questions.filter(q => {
    const typeMatch = filterType === 'all' || q.type === filterType
    const difficultyMatch = difficulty === 'all' || q.difficulty === difficulty
    return typeMatch && difficultyMatch
  })
  
  const currentQuestion = filteredQuestions[currentIndex]

  // Load today's progress
  useEffect(() => {
    const today = new Date().toDateString()
    const todayAttempts = state.progress.oir?.attempts?.filter(
      a => new Date(a.timestamp).toDateString() === today
    ) || []
    setAnsweredToday(todayAttempts.length)
  }, [state.progress.oir])

  // Calculate statistics
  const stats = {
    totalAttempted: state.progress.oir?.attempts?.length || 0,
    correctAnswers: state.progress.oir?.attempts?.filter(a => a.correct).length || 0,
    accuracy: state.progress.oir?.attempts?.length > 0 
      ? Math.round((state.progress.oir.attempts.filter(a => a.correct).length / state.progress.oir.attempts.length) * 100)
      : 0,
    todayProgress: Math.round((answeredToday / dailyGoal) * 100),
    streak: calculateStreak(state.progress.oir?.attempts || [])
  }

  function calculateStreak(attempts) {
    if (attempts.length === 0) return 0
    let currentStreak = 0
    const today = new Date()
    
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(checkDate.getDate() - i)
      const dateStr = checkDate.toDateString()
      
      const hasActivity = attempts.some(a => 
        new Date(a.timestamp).toDateString() === dateStr
      )
      
      if (hasActivity) {
        currentStreak++
      } else if (i > 0) {
        break
      }
    }
    
    return currentStreak
  }

  const handleAnswer = () => {
    if (!selectedAnswer) return

    const isCorrect = scoreMCQ(selectedAnswer, currentQuestion.answer)
    setShowFeedback(true)
    setAttempted(attempted + 1)
    
    if (isCorrect) {
      setScore(score + 1)
      setStreak(streak + 1)
    } else {
      setStreak(0)
    }

    // Save progress
    updateState('progress.oir.attempts', [
      ...(state.progress.oir?.attempts || []),
      {
        questionId: currentQuestion.id,
        answer: selectedAnswer,
        correct: isCorrect,
        timestamp: new Date().toISOString(),
        type: currentQuestion.type || 'general',
        difficulty: currentQuestion.difficulty || 'medium'
      },
    ])
    
    updateState('progress.oir.completed', attempted + 1)
    setAnsweredToday(answeredToday + 1)
  }

  const handleNext = () => {
    setSelectedAnswer(null)
    setShowFeedback(false)
    setCurrentIndex((currentIndex + 1) % filteredQuestions.length)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading questions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Questions</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (filteredQuestions.length === 0) {
    return (
      <div className="card">
        <p className="text-center text-gray-600 dark:text-gray-400">
          No questions available for the selected filters.
        </p>
      </div>
    )
  }

  const isCorrect = showFeedback && selectedAnswer === currentQuestion.answer
  const progressPercentage = Math.round(((currentIndex + 1) / filteredQuestions.length) * 100)

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-600" />
              OIR Practice
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Officer Intelligence Rating
            </p>
          </div>
          
          <button
            onClick={() => setShowStats(!showStats)}
            className="btn-secondary flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            {showStats ? 'Hide' : 'Show'} Stats
          </button>
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-700">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-600" />
              <div>
                <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                  {stats.streak}
                </div>
                <div className="text-xs text-orange-700 dark:text-orange-300">Day Streak</div>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {stats.accuracy}%
                </div>
                <div className="text-xs text-green-700 dark:text-green-300">Accuracy</div>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {score}/{attempted}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">Today's Score</div>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {answeredToday}/{dailyGoal}
                </div>
                <div className="text-xs text-purple-700 dark:text-purple-300">Daily Goal</div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Progress Bar */}
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Today's Progress</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {stats.todayProgress}%
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${stats.todayProgress}%` }}
            />
          </div>
        </div>

        {/* Detailed Stats (Collapsible) */}
        {showStats && (
          <div className="card animate-fadeIn">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Detailed Statistics
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold text-primary-600">
                  {stats.totalAttempted}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Questions Attempted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {stats.correctAnswers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Correct Answers</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="font-medium">Filter Questions</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value)
                setCurrentIndex(0)
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
            >
              <option value="all">All Types</option>
              <option value="verbal">Verbal Reasoning</option>
              <option value="non-verbal">Non-Verbal Reasoning</option>
              <option value="current-affairs">Current Affairs</option>
              <option value="general-knowledge">General Knowledge</option>
              <option value="defence">Defence Related</option>
            </select>

            <select
              value={difficulty}
              onChange={(e) => {
                setDifficulty(e.target.value)
                setCurrentIndex(0)
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
            >
              <option value="all">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="card transform transition-all duration-300 hover:shadow-lg">
        <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Question {currentIndex + 1} of {filteredQuestions.length}
            </span>
            
            {currentQuestion.type && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                {currentQuestion.type === 'non-verbal' && <ImageIcon className="w-3 h-3" />}
                {currentQuestion.type === 'current-affairs' && <BookOpen className="w-3 h-3" />}
                {currentQuestion.type}
              </span>
            )}
            
            {currentQuestion.difficulty && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentQuestion.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                currentQuestion.difficulty === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
                'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
              }`}>
                {currentQuestion.difficulty}
              </span>
            )}

            {currentQuestion.isNew && (
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-orange-400 to-pink-500 text-white animate-pulse">
                NEW
              </span>
            )}

            {currentQuestion.newsDate && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                📰 {new Date(currentQuestion.newsDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        <div className="progress-bar mb-6">
          <div
            className="progress-fill transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Question Image (if non-verbal) */}
        {currentQuestion.imageUrl && (
          <div className="mb-6 flex justify-center">
            <img
              src={currentQuestion.imageUrl}
              alt="Question visual"
              className="max-w-full max-h-80 rounded-lg shadow-md border-2 border-gray-200 dark:border-gray-700"
            />
          </div>
        )}

        <h3 className="text-lg font-semibold mb-4 leading-relaxed">
          {currentQuestion.text}
        </h3>

        {/* News Context (if from current affairs) */}
        {currentQuestion.newsContext && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
            <div className="flex items-start gap-2">
              <BookOpen className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <strong>Context:</strong> {currentQuestion.newsContext}
              </div>
            </div>
          </div>
        )}

        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const isSelected = selectedAnswer === option
            const isCorrectOption = option === currentQuestion.answer
            let optionClass = 'p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 transform '

            if (showFeedback) {
              if (isCorrectOption) {
                optionClass += 'border-green-500 bg-green-50 dark:bg-green-900/20 scale-105 '
              } else if (isSelected && !isCorrectOption) {
                optionClass += 'border-red-500 bg-red-50 dark:bg-red-900/20 '
              } else {
                optionClass += 'border-gray-300 dark:border-gray-600 opacity-50 '
              }
            } else if (isSelected) {
              optionClass += 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 scale-105 '
            } else {
              optionClass += 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-102 '
            }

            return (
              <button
                key={index}
                onClick={() => !showFeedback && setSelectedAnswer(option)}
                disabled={showFeedback}
                className={optionClass}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-left">{option}</span>
                  {showFeedback && isCorrectOption && (
                    <CheckCircle className="w-5 h-5 text-green-600 animate-bounce" />
                  )}
                  {showFeedback && isSelected && !isCorrectOption && (
                    <XCircle className="w-5 h-5 text-red-600 animate-shake" />
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Feedback Section */}
        {showFeedback && (
          <div className={`mt-6 p-4 rounded-lg animate-fadeIn ${
            isCorrect 
              ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-2 border-green-300' 
              : 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-2 border-red-300'
          }`}>
            <div className="flex items-start mb-3">
              {isCorrect ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-lg text-green-800 dark:text-green-200">
                      Excellent! 🎉
                    </span>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      You're on a roll! Keep it up!
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-lg text-red-800 dark:text-red-200">
                      Not quite right
                    </span>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      Learn from mistakes and move forward!
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="mt-3 p-3 bg-white dark:bg-gray-800/50 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong className="text-primary-600">Explanation:</strong> {currentQuestion.explanation}
              </p>
              
              {currentQuestion.learnMore && (
                <a 
                  href={currentQuestion.learnMore}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  <Zap className="w-3 h-3" />
                  Learn more
                </a>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          {!showFeedback ? (
            <button
              onClick={handleAnswer}
              disabled={!selectedAnswer}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:scale-105 transition-transform"
            >
              <CheckCircle className="w-5 h-5" />
              Submit Answer
            </button>
          ) : (
            <button 
              onClick={handleNext} 
              className="btn-primary flex-1 flex items-center justify-center gap-2 transform hover:scale-105 transition-transform"
            >
              Next Question
              <TrendingUp className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Motivational Messages */}
      {answeredToday >= dailyGoal && (
        <div className="card bg-gradient-to-r from-green-500 to-emerald-600 text-white animate-fadeIn">
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8" />
            <div>
              <h3 className="font-bold text-lg">Daily Goal Completed! 🎯</h3>
              <p className="text-sm text-green-100">
                Great job! You've completed your daily goal. Keep the momentum going!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
